import { motion } from "framer-motion";
import { Users, TrendingUp, DollarSign, Rocket } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
};

const results = [
  {
    icon: Users,
    metric: "+Clientes",
    title: "Mais clientes todos os dias",
    description: "Fluxo constante de leads qualificados entrando em contato.",
  },
  {
    icon: TrendingUp,
    metric: "Previsível",
    title: "Crescimento previsível",
    description: "Saber exatamente quanto vai faturar no próximo mês.",
  },
  {
    icon: DollarSign,
    metric: "+Receita",
    title: "Aumento do faturamento",
    description: "Mais vendas, mais lucro, mais estabilidade financeira.",
  },
  {
    icon: Rocket,
    metric: "Escala",
    title: "Escala do negócio",
    description: "Estrutura para crescer sem depender apenas de você.",
  },
];

export const ResultsSection = () => {
  return (
    <section
      id="resultados"
      data-testid="results-section"
      className="relative py-24 lg:py-32 bg-[#050505] section-gradient-green"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          {...fadeInUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 lg:mb-20"
        >
          <span className="font-outfit uppercase tracking-[0.2em] text-xs font-semibold text-[#10B981] mb-4 block">
            Transformação
          </span>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Resultados Esperados
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            O que você pode esperar ao trabalhar com a Andiaras.
          </p>
        </motion.div>

        {/* Results Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {/* Large Card */}
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6 }}
            data-testid="result-main"
            className="md:col-span-8 glass-card bento-card rounded-2xl p-10 lg:p-12 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#10B981]/10 rounded-full blur-[100px]" />
            <div className="relative">
              <div className="w-16 h-16 rounded-xl bg-[#10B981]/10 border border-[#10B981]/20 flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-[#10B981]" />
              </div>
              <span className="metric-number text-5xl lg:text-6xl font-bold block mb-4">
                +Clientes
              </span>
              <h3 className="font-outfit font-semibold text-2xl text-white mb-3">
                Mais clientes todos os dias
              </h3>
              <p className="text-[#A1A1AA] text-lg max-w-lg">
                Fluxo constante de leads qualificados entrando em contato, sem depender de indicações ou sorte.
              </p>
            </div>
          </motion.div>

          {/* Small Card */}
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="result-predictable"
            className="md:col-span-4 glass-card bento-card rounded-2xl p-8 flex flex-col justify-center"
          >
            <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 border border-[#10B981]/20 flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-[#10B981]" />
            </div>
            <span className="metric-number text-3xl font-bold block mb-2">
              Previsível
            </span>
            <h3 className="font-outfit font-semibold text-lg text-white mb-2">
              Crescimento previsível
            </h3>
            <p className="text-[#A1A1AA] text-sm">
              Saber exatamente quanto vai faturar.
            </p>
          </motion.div>

          {/* Small Card */}
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="result-revenue"
            className="md:col-span-4 glass-card bento-card rounded-2xl p-8 flex flex-col justify-center"
          >
            <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 border border-[#10B981]/20 flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-[#10B981]" />
            </div>
            <span className="metric-number text-3xl font-bold block mb-2">
              +Receita
            </span>
            <h3 className="font-outfit font-semibold text-lg text-white mb-2">
              Aumento do faturamento
            </h3>
            <p className="text-[#A1A1AA] text-sm">
              Mais vendas, mais lucro, mais estabilidade.
            </p>
          </motion.div>

          {/* Large Card */}
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            data-testid="result-scale"
            className="md:col-span-8 glass-card bento-card rounded-2xl p-10 lg:p-12 relative overflow-hidden"
          >
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#2563EB]/10 rounded-full blur-[100px]" />
            <div className="relative">
              <div className="w-16 h-16 rounded-xl bg-[#2563EB]/10 border border-[#2563EB]/20 flex items-center justify-center mb-6">
                <Rocket className="w-8 h-8 text-[#2563EB]" />
              </div>
              <span className="font-outfit font-bold text-5xl lg:text-6xl text-[#2563EB] block mb-4">
                Escala
              </span>
              <h3 className="font-outfit font-semibold text-2xl text-white mb-3">
                Escala do negócio
              </h3>
              <p className="text-[#A1A1AA] text-lg max-w-lg">
                Estrutura sólida para crescer de forma sustentável, sem depender apenas de você ou de esforços manuais.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
